import { CircularProgress, Grid, IconButton } from '@material-ui/core';
import {
  CancelIcon,
  LeftArrowIcon,
  RightArrowIcon,
  SaveIcon,
  SmallRightArrowIcon,
} from 'app/assets/images/icons';
import ErrorBoundary from 'app/components/ErrorBoundary';
import MainBreadcrumbs from 'app/components/MainBreadcrumbs';
import { useCustomForm } from 'app/hooks/useCustomForm';
import { _t } from 'app/utils/i18n';
import React, {
  createContext,
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ReportInformation from 'reports/components/ReportInformation';
import { schema } from './schema';
import styles from './AddNewReport.module.scss';
import SecheduleReport from 'reports/components/SecheduleReport';
import AddComments from 'reports/components/AddComments';
import { constants, getFromLocalStorage } from 'app/utils/localStorage';
import { getSearchState } from 'app/utils/search-state';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  getActiveTemplates,
  showReport,
  storeReport,
  templateToReport,
  updateReport,
} from 'reports/Requests';
import Loader from 'app/components/Loader';
import { toast } from 'react-toastify';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useModal } from 'app/context/Modal';
import { path } from 'app/utils/path';
import { dataTransformer, transformData } from './transformers';

import ReportTypography from 'reports/components/ReportTypography';
import { handleDownloadDetailedSheet } from 'hashtags/Requests';
import {
  getInfTweetsFilters,
  getInfTweetsInfoFromSlug,
} from 'reports/utils/influencer-tweet-handlers';
import { getTabNameFromSlug } from '../AddTemplate/constants';

const defaultValues: any = {
  report_template: null as any,
  description: '',
  sections: [{ title: '', description: '' }],
  charts: [],
  intervals: 'DAILY',
  days_of_Week: '',
  email_subject: '',
  recipients: null,
  schedule_description: '',
  format: ['word'],
  days: [''],
  day_of_month: '',
  slugs: '',
  report_id: null,
  start_date: null,
  schedule_status: false,
  allow_blank_description: false,
  isCompare: false,
};

interface ReportContextType {
  changeHandler: (key: string, value: any) => void;
  values: typeof defaultValues;
  renderCharts: any;
  renderTweets: any;
  setError: any;
  errors: any;
  clearErrors: any;
}

interface IReportProvider extends ReportContextType {
  children?: React.ReactNode;
}

export const ReportProvider = ({
  children,
  values,
  changeHandler,
  setError,
  errors,
  clearErrors,
}: IReportProvider): JSX.Element | null => {
  const params = useParams() as any;
  const isInfluencerModule = params?.module === 'INFLUENCERS';
  const isCompare = values?.type === 'COMPARE' || values?.isCompare;
  const filterSlugs = (slugs: any) => {
    return slugs?.filter((i: any) => i?.is_active);
  };
  const { data } = useQuery(
    [
      'templateInfo',
      {
        template_id: values?.report_template?.value,
        item_ids: values?.items?.map((item: any) => item?.id),
      },
    ],
    templateToReport,
    {
      onSuccess: (data) => {
        changeHandler('charts', data?.selected_slugs);
      },
      enabled: !values.charts?.length,
      staleTime: 0,
      refetchOnMount: true,
      cacheTime: 0,
      keepPreviousData: false,
      refetchOnReconnect: true,
    },
  );

  const selected_slugs =
    filterSlugs(data?.selected_slugs) || filterSlugs(values?.charts) || [];

  const renderCharts = (charts: any) => {
    const chartsFilter = charts
      ?.filter((item: any) => {
        if (
          isCompare &&
          item?.id?.includes('hashtags_tweets_column') &&
          selected_slugs?.some((i: any) =>
            i?.slug?.includes('hashtags_tweets_column'),
          )
        ) {
          return true;
        }

        return selected_slugs?.some((i: any) => {
          return i?.slug === item?.id;
        });
      })
      .map((item: any) => ({
        ...item,
        order:
          item?.id?.includes('hashtags_tweets_column') && isCompare
            ? 200
            : selected_slugs.findIndex((i: any) => i.slug === item?.id),
      }));

    const paragraph = selected_slugs
      ?.map((item: any) => {
        return item?.module_slug_id === 0
          ? {
              ...item,
              id: item.id,
              order: item.order,
              component: (
                <ReportTypography
                  {...item}
                  itemsName={values?.items?.map((item: any) => item.name)}
                  moduleName={values?.moduleName}
                  changeHandler={changeHandler}
                  values={values}
                  setError={setError}
                  errors={errors}
                  clearErrors={clearErrors}
                  key={item?.id}
                />
              ),
            }
          : false;
      })
      .filter(Boolean);

    const staticParagraphsData = [
      {
        slug: 'report_name',
        order: -2,
      },
      {
        slug: 'report_summary',
        paragraph: values?.description,
        order: -2,
      },
      // ...(Array.isArray(values.sections)
      //   ? values.sections.map((i) => ({
      //       slug: 'sections',
      //       ...i,
      //     }))
      //   : []),
    ];
    const staticParagraphs = staticParagraphsData.map((item, i) => {
      return {
        ...item,
        component: (
          <ReportTypography
            {...item}
            itemsName={values?.items?.map((item: any) => item.name)}
            moduleName={values?.moduleName}
            values={values}
            isStatic
            key={i}
          />
        ),
      };
    });

    return [...staticParagraphs, ...chartsFilter, ...paragraph]
      .sort((a, b) => a.order - b.order)
      .map((item, i) =>
        item.component
          ? React.cloneElement(item.component, { key: item?.id || i })
          : item.component,
      );
  };

  const renderTweets = () => {
    const tweetColumns = selected_slugs
      .filter((chart: any) => chart?.chart_type === 'tweetColumn')
      .map((item: any) =>{ 
        const isHashtag=item?.slug?.includes('hashtags')
        const inf={influencer_id: values?.items?.map((item: any) => item?.id)[0]}
        const hashtag={hashtag_id: values?.items?.map((item: any) => item?.id)[0]}
        let post_type=item?.slug?.includes('instagram')
        ? {post_type:item?.instagram_post_type?.value}
        : item?.slug?.includes('linkedin')
        ? {post_type:item?.post_type?.value}
        : {tweet_type: item?.tweet_type_id}
        
        return {
        type: getInfTweetsInfoFromSlug(item.slug, isInfluencerModule,isHashtag)?.type,
        requestUrl: getInfTweetsInfoFromSlug(item.slug, isInfluencerModule,isHashtag)
          ?.requestUrl,
        hideSort: true,
        isReport: true,
        tabName: getTabNameFromSlug(item?.slug),
        customFilters: {
          ...(isHashtag?hashtag:inf),
          limit: item?.item_count,
          order_column: item?.item_sort_type?.sort_colum,
          type_of_sort: item?.sort_type,
          ...post_type,
          ...getInfTweetsFilters(item),
        },
        id: item?.slug,
        chart_id: item.key,
      }});

    return tweetColumns;
  };

  const value = useMemo(
    () =>
      ({
        values,
        changeHandler,
        renderCharts,
        renderTweets,
      }) as any,

    [values, selected_slugs],
  );
  return (
    <ReportContext.Provider value={value}>{children}</ReportContext.Provider>
  );
};

const isEn = getFromLocalStorage('LNG') == 'en' ? true : false;

const ReportContext = createContext<ReportContextType | null>(null);
const requiredKeys: any = {
  0: ['report_template', 'description', 'sections'],
  1: [
    'email_subject',
    'recipients',
    'schedule_description',
    'days',
    'start_date',
    'format',
    'recipients_cc',
    'recipients_bcc',
    'day_of_month',
  ],
};
const AddNewReport: FunctionComponent = () => {
  const [search] = useState(getSearchState());
  const [activeStep, setActiveStep] = useState(0);
  const params = useParams() as any;
  const isDark = getFromLocalStorage(constants.darkMode);
  const id = search?.id;
  const name =
    search?.name !== ''
      ? search?.name
      : search?.values?.map((item: any) => item.name).join(' V.S. ');
  const moduleName = search?.moduleName || params?.module;
  const { isLoading: showReportIsLoading } = useQuery(
    ['showReport', { report_id: +params?.id || +search?.report_id }],
    showReport,
    {
      enabled: params?.id !== undefined || search?.report_id !== undefined,
      staleTime: 0,
      refetchOnMount: true,
      cacheTime: 0,
      keepPreviousData: false,
      refetchOnReconnect: true,
      onSuccess: (res) => {
        if (res?.template?.status == 'TRASHED') {
          history.push('/404');
          return;
        }

        reset(transformData(res, +params?.id || search?.report_id));
      },
    },
  );
  const {
    register,
    formState: { errors, isSubmitting },
    control,
    getValues,
    changeHandler,
    handleSubmit,
    setError,
    trigger,
    reset,
    clearErrors,
  } = useCustomForm(schema, {
    ...defaultValues,
    itemsName: name,
    moduleName,
    items: Array.isArray(id) ? search?.values : [{ id: +id, name }],
    isCompare: Array.isArray(id),
  });

  const history = useHistory();
  const values = getValues();
  const modal = useModal();

  const isGroup = params?.type === 'GROUP';

  const isCompare = values?.type === 'COMPARE' || Array.isArray(id);

  const { mutate: downloadDetailedSheet } = useMutation(
    handleDownloadDetailedSheet,
    {
      onSuccess: ({ payload }) => {
        modal.show({
          message: _t(
            `reports:working_on_downloading_you_will_be_notified_once_finished`,
            {
              report_name: payload?.report_name,
              template_name: payload?.template_name,
            },
          ),
          onCancelText: _t('basic:ok'),
        });
      },
    },
  );
  const { mutate: storeReportMutate, isLoading: storeReportMutateIsLoading } =
    useMutation(storeReport, {
      onSuccess: (res) => {
        history.replace(
          `${path.reports.EDIT_REPORT_URI}/${moduleName}${
            isGroup ? '/GROUP' : ''
          }/${res?.id}`,
        );
        toast.success(_t('reports:report_added'));
        downloadDetailedSheet({ id: { value: res?.id } });
      },
    });
  const { mutate: updateReportMutate, isLoading: updateReportIsLoading } =
    useMutation(updateReport, {
      onSuccess: (res) => {
        toast.success(_t('reports:report_update'));
        downloadDetailedSheet({ id: { value: res?.id } });
      },
    });

  const queryClient = useQueryClient();

  const validateTitles = () => {
    const slugs = ['paragraph', 'sub_title', 'title'];
    const names = {
      sub_title: 'report_sub_title',
      paragraph: 'paragraph',
      title: 'title',
    } as { [key: string]: string };

    if (Object.keys(errors)?.length !== 0) return false;

    const titles = values?.charts?.filter((item: any) =>
      slugs.includes(item.slug),
    );
    let isValid = true;

    for (let index = 0; index < titles?.length; index++) {
      const element = titles[index] as any;
      if (element?.paragraph?.length < 2) {
        isValid = false;

        setError(`${element?.slug}_${element?.id}`, {
          message: _t('basic:err_min', {
            fieldName: _t(`reports:${names?.[element?.slug]}`),
            length: 2,
          }),
        });
      }
    }
    if (!isValid && activeStep !== 2) {
      setActiveStep(2);
    }
    return isValid;
  };
  useEffect(() => {
    return () => {
      queryClient.removeQueries(['getActiveTemplates']);
    };
  }, []);
  const { data: activeTemplates, isLoading: activeTemplatesIsLoading } =
    useQuery(
      [
        'getActiveTemplates',
        {
          module_name: moduleName?.toUpperCase(),
          item_ids: Array.isArray(id) ? id : [+id],
          sub_module_name: isGroup ? 'GROUPS' : undefined,
          type: isGroup ? 'GROUP' : undefined,
        },
      ],
      getActiveTemplates,
    );

  const pages = [
    { title: _t('reports:report') },
    {
      title: _t(
        params?.id !== undefined || values?.status === 'ACTIVE'
          ? 'reports:edit_report'
          : 'reports:create_report',
      ),
    },
  ];
  const steps = [
    {
      title: _t('reports:report_information'),
      component: (
        <ErrorBoundary>
          <ReportInformation
            onChange={changeHandler}
            {...{
              getValues,
              register,
              values,
              control,
              moduleName,
              name,
              errors,
              setError,
              trigger,
              activeTemplates,
            }}
          />
        </ErrorBoundary>
      ),
    },

    {
      title: _t('reports:schedule_report'),
      component: (
        <ErrorBoundary>
          <SecheduleReport
            key={params?.id}
            onChange={changeHandler}
            {...{
              trigger,
              register,
              values,
              control,
              errors,
              setError,
              clearErrors,
            }}
          />
        </ErrorBoundary>
      ),
    },

    {
      title: _t('reports:report_data'),
      component: (
        <ReportProvider
          values={values}
          changeHandler={changeHandler}
          setError={setError}
          errors={errors}
          clearErrors={clearErrors}
          renderCharts={undefined}
          renderTweets={[]}
        >
          <AddComments
            {...{
              moduleName,
              isCompare,
              items:getValues()?.charts
            }}
          />
        </ReportProvider>
      ),
    },
  ];
  if (isGroup) steps.pop();
  const renderCurrentComponent = () => {
    return steps?.[activeStep]?.component;
  };

  const activeReq = (i = activeStep) =>
    Array.isArray(requiredKeys?.[i])
      ? !values?.schedule_status && activeStep === 1
        ? []
        : requiredKeys?.[i]
      : [];

  const transformerData = {
    isCompare,
    id: params?.id || id,
    compareValues: values?.items || search?.values,
    name,
    params,
    values,
    isGroup,
  };
  const handleNext = async () => {
    if (!validateTitles() && activeStep !== 0) return;
    const res = await trigger(activeReq());
    if (
      values?.intervals === 'DAILY' &&
      values.days?.[0]?.length === 0 &&
      !res
    ) {
      setError('days', {
        message: _t('err_required'),
      });
      return;
    }

    if (res) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePrev = async () => {
    if (activeStep === 1) {
      const res = await trigger(activeReq());
      if (
        values?.intervals === 'DAILY' &&
        values.days?.[0]?.length === 0 &&
        !res
      ) {
        setError('days', {
          message: _t('err_required'),
        });
        return;
      }
      res && setActiveStep(activeStep - 1);
      return;
    }
    if (activeStep === 2 && validateTitles()) {
      setActiveStep(activeStep - 1);
      return;
    }
  };
  const location = useLocation<any>();
  const onSubmit = () => {
    if (!validateTitles()) return;
    setTimeout(() => {
      if (!params?.id) {
        storeReportMutate(dataTransformer(transformerData));
      } else {
        updateReportMutate(dataTransformer(transformerData));
      }
    }, 0);
  };
  const openModal = () => {
    modal.show({
      message: _t(
        `reports:${
          params?.id !== undefined || values?.status === 'ACTIVE'
            ? 'are_you_sure_you_want_to_cancel_edit_report'
            : 'are_you_sure_you_want_to_cancel_add_report'
        }`,
      ),
      onOk: () => {
        modal.hide();
        if (getSearchState()?.prevUrl) {
          history.replace(getSearchState()?.prevUrl);
        } else if (location.state?.prevUrl) {
          history.replace(location.state.prevUrl);
        } else if (history.length > 2) {
          history.goBack();
        } else if (values?.report_template?.value) {
          history.replace(
            `${path.reports.TEMPLATE_DETAILS_URI}/${moduleName}/${values?.report_template?.value}`,
          );
        }
      },
    });
  };

  if (activeTemplatesIsLoading || showReportIsLoading) {
    return <Loader />;
  }

  const isSubmit =
    params?.id || (isGroup ? activeStep === 1 : activeStep === 2);

  return (
    <div className={styles.AddNewReport}>
      <div className="TopBar">
        <MainBreadcrumbs pages={pages} compId="reports_addNewReport" />
        <div className="Controls">
          <IconButton
            type="submit"
            disabled={!isSubmit}
            onClick={
              Object.keys(errors)?.length === 0
                ? handleSubmit(onSubmit)
                : undefined
            }
            style={{
              opacity: !isSubmit ? 0.5 : 1,
            }}
          >
            {storeReportMutateIsLoading || updateReportIsLoading ? (
              <CircularProgress
              size="30px"
              style={{color:isDark?'white':'inherit'}}
              data-testid="circular-progress-test"
              />
            ) : (
              <SaveIcon />
            )}
          </IconButton>
          <IconButton onClick={openModal}>
            <CancelIcon />
          </IconButton>
        </div>
      </div>
      <Grid container spacing={4}>
        <Grid item md={4}>
          {steps.map((step, index) => (
            <div
              key={step.title}
              className={`${styles.Step} ${
                activeStep == index ? 'active' : ''
              }`}
              onClick={async () => {
                if (
                  values?.intervals === 'DAILY' &&
                  values.days?.[0]?.length === 0 &&
                  values.schedule_status
                ) {
                  setError('days', {
                    message: _t('err_required'),
                  });
                  return;
                }
                if (activeStep === 2) {
                  validateTitles() && setActiveStep(index);
                  return;
                }
                const res = await trigger(activeReq());
                const prevRes = await trigger(activeReq(index - 1));

                if (res && prevRes) {
                  setActiveStep(index);
                }
              }}
            >
              <p>{step.title}</p>
              <SmallRightArrowIcon />
            </div>
          ))}
        </Grid>
        <Grid item md={8}>
          <div className={styles.Container}>
            <div>{renderCurrentComponent()}</div>

            <div className={styles.controlsButton}>
              <IconButton
                onClick={handlePrev}
                disabled={activeStep === 0}
                style={{ opacity: activeStep === 0 ? 0.5 : 1 }}
              >
                {isEn ? <LeftArrowIcon /> : <RightArrowIcon />}
              </IconButton>
              {(isGroup && activeStep === 1) || activeStep === 2 ? (
                <IconButton
                  type="submit"
                  disabled={isSubmitting}
                  onClick={
                    Object.keys(errors)?.length === 0
                      ? handleSubmit(onSubmit)
                      : undefined
                  }
                >
                  <SaveIcon />
                </IconButton>
              ) : (
                <IconButton onClick={handleNext}>
                  {storeReportMutateIsLoading || updateReportIsLoading ? (
                    <CircularProgress
                    size="30px"
                    style={{color:isDark?'white':'inherit'}}
                    data-testid="circular-progress-test"
                    />
                  ) : isEn ? (
                    <RightArrowIcon />
                  ) : (
                    <LeftArrowIcon />
                  )}
                </IconButton>
              )}
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export const useReport = (): ReportContextType | null => {
  const context = useContext(ReportContext);
  if (!context) return null;
  return context;
};
export default AddNewReport;
